const areEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const getSection = (document = {}) => (
  document.sections && document.sections.emergency_contacts
    ? document.sections.emergency_contacts
    : {}
);

const getTypes = (document) => {
  const data = getSection(document).data || {};
  return Array.isArray(data.types) ? data.types : [];
};

const addItemChange = (itemsByType, type, change) => {
  const typeId = type && type.id !== undefined ? type.id : null;
  const key = `${typeId}`;
  if (!itemsByType.has(key)) {
    itemsByType.set(key, {
      item_id: typeId,
      item_label: type && type.emergency_type ? type.emergency_type : null,
      section_changes: [{
        section_key: "emergency_contacts",
        section_label: null,
        changes: []
      }]
    });
  }
  itemsByType.get(key).section_changes[0].changes.push(change);
};

const contactValue = (contact, type) => ({
  id: contact.id,
  contact_name: contact.contact_name,
  phone_number: contact.phone_number,
  emergency_type: type && type.emergency_type
});

export const buildEmergencyContactsChanges = (initialDocument = {}, submittedDocument = {}) => {
  const initialSection = getSection(initialDocument);
  const submittedSection = getSection(submittedDocument);
  const initialData = initialSection.data || {};
  const submittedData = submittedSection.data || {};
  const sectionLabel = submittedSection.display_name;
  const itemsByType = new Map();

  const sectionChanges = [];
  if (!areEqual(Number(initialData.toggle), Number(submittedData.toggle))) {
    sectionChanges.push({
      field_name: "toggle",
      field_label: "Emergency Contacts Toggle",
      change_type: "inline",
      action: Number(submittedData.toggle) === 1 ? "toggled_on" : "toggled_off",
      previous_value: Number(initialData.toggle),
      new_value: Number(submittedData.toggle)
    });
  }

  const initialTypes = getTypes(initialDocument);
  const submittedTypes = getTypes(submittedDocument);
  const initialTypesById = new Map(initialTypes.map((type) => [type.id, type]));
  const submittedTypesById = new Map(submittedTypes.map((type) => [type.id, type]));
  const typeIds = new Set([...initialTypesById.keys(), ...submittedTypesById.keys()]);
  const initialContacts = new Map();
  const submittedContacts = new Map();

  initialTypes.forEach((type) => (type.contacts || []).forEach((contact) => {
    initialContacts.set(contact.id, { contact, type });
  }));
  submittedTypes.forEach((type) => (type.contacts || []).forEach((contact) => {
    submittedContacts.set(contact.id, { contact, type });
  }));

  typeIds.forEach((typeId) => {
    const initialType = initialTypesById.get(typeId);
    const submittedType = submittedTypesById.get(typeId);
    if (!initialType || !submittedType) return;

    if (!areEqual(initialType.emergency_type, submittedType.emergency_type)) {
      addItemChange(itemsByType, submittedType, {
        field_name: "emergency_type",
        field_label: "Emergency Type",
        change_type: "inline",
        action: "updated",
        previous_value: initialType.emergency_type,
        new_value: submittedType.emergency_type
      });
    }

    if (!areEqual(Number(initialType.toggle), Number(submittedType.toggle))) {
      addItemChange(itemsByType, submittedType, {
        field_name: "toggle",
        field_label: "Emergency Type Toggle",
        change_type: "inline",
        action: Number(submittedType.toggle) === 1 ? "toggled_on" : "toggled_off",
        previous_value: Number(initialType.toggle),
        new_value: Number(submittedType.toggle)
      });
    }
  });

  const contactIds = new Set([...initialContacts.keys(), ...submittedContacts.keys()]);
  contactIds.forEach((contactId) => {
    const initialEntry = initialContacts.get(contactId);
    const submittedEntry = submittedContacts.get(contactId);
    if (!initialEntry || !submittedEntry) {
      const entry = submittedEntry || initialEntry;
      addItemChange(itemsByType, entry.type, {
        field_name: "contacts",
        field_label: "Contacts",
        change_type: "multiple_items",
        action: submittedEntry ? "added" : "deleted",
        previous_value: submittedEntry ? [] : [contactValue(initialEntry.contact, initialEntry.type)],
        new_value: submittedEntry ? [contactValue(submittedEntry.contact, submittedEntry.type)] : []
      });
      return;
    }

    if (initialEntry.type.id !== submittedEntry.type.id) {
      addItemChange(itemsByType, submittedEntry.type, {
        item_id: contactId,
        item_label: submittedEntry.contact.contact_name,
        field_name: "emergency_type",
        field_label: "Emergency Type",
        change_type: "inline",
        action: "updated",
        previous_value: initialEntry.type.emergency_type,
        new_value: submittedEntry.type.emergency_type
      });
      return;
    }

    ["contact_name", "phone_number"].forEach((fieldName) => {
      if (!areEqual(initialEntry.contact[fieldName], submittedEntry.contact[fieldName])) {
        addItemChange(itemsByType, submittedEntry.type, {
          item_id: contactId,
          item_label: submittedEntry.contact.contact_name,
          field_name: fieldName,
          field_label: fieldName === "contact_name" ? "Contact Name" : "Phone Number",
          change_type: "inline",
          action: "updated",
          previous_value: initialEntry.contact[fieldName],
          new_value: submittedEntry.contact[fieldName]
        });
      }
    });
  });

  if (sectionChanges.length > 0) {
    itemsByType.set("__section__", {
      item_id: null,
      item_label: null,
      section_changes: [{
        section_key: "emergency_contacts",
        section_label: sectionLabel,
        changes: sectionChanges
      }]
    });
  }

  return Array.from(itemsByType.values()).map((item) => ({
    ...item,
    section_changes: item.section_changes.map((section) => ({
      ...section,
      section_label: sectionLabel
    }))
  }));
};
